import Service, { service } from '@ember/service';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import { saveRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import type { UploadFile } from 'ember-file-upload';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { DigitalAssetTrait } from 'tio-data/data/traits/digital-asset.type';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type DigitalAssetsService from './digital-assets';
import type FeaturesService from './features.ts';

type SerializableValue =
  string | number | boolean | null | SerializableValue[] | SerializableRecord;
type SerializableRecord = { [key: string]: SerializableValue };
type ObservableDocumentPayload = {
  data: [
    {
      type: 'observable-document';
      id: string;
      attributes: SerializableRecord;
    },
  ];
};

// Fallback content types by extension, used only when the browser doesn't report
// a MIME type (uploadFile.file.type === ''). Guessing 'application/pdf' for every
// unknown type was harmless while only PDFs were accepted, but now that PNG/JPEG
// are also allowed for manual-entry flows, mislabeling a typeless image as a PDF
// would let it slip past validation under the wrong content type.
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

const inferContentType = (file: File): string => {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  // A neutral, unrecognized-content-type default -- never guess 'application/pdf'
  // for an unknown extension, or a non-PDF file with no reported MIME type could
  // masquerade as a PDF and slip past the backend's content-type validation.
  return CONTENT_TYPE_BY_EXTENSION[extension] ?? 'application/octet-stream';
};

export type ObservableDocumentAttributes = SerializableRecord & { id: string };
export default class ObservablesService extends Service {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare features: FeaturesService;

  /**
   * Uploads to S3, saves the asset, then creates a document for the person.
   * @param uploadFile Browser file and its binary-upload operation.
   * @param options Owner, optional batch, and upload or manual-entry classification.
   * @returns The saved document for extraction or manual-entry processing.
   * @throws When signed-URL creation, binary upload, or either API save fails.
   */
  createObservableDocument = async (
    uploadFile: UploadFile,
    options: { person: PersonModel; batch?: string; via?: string }
  ): Promise<ObservableDocumentModel> => {
    const { person, batch, via = 'UPLOAD' } = options;

    // get API path for observable assets
    // WarpDrive migration Phase 2.4: buildBaseURL replaces adapter.urlForCreateRecord.
    const collectionPathOpts: QueryUrlOptions = {
      identifier: { type: 'observable-assets' },
      op: 'query',
      resourcePath: 'observable-assets',
    };
    const collectionPath = buildBaseURL(collectionPathOpts);

    // WarpDrive migration Phase 2.4: buildTioAuthHeaders replaces adapter.headers
    // for direct fetch calls (presigned-S3 upload path).
    const headers = buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
      contentType: 'application/vnd.api+json',
    });

    // create asset record through API
    const assetResponse = await fetch(`${collectionPath}/s3-signed-url`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ filename: uploadFile.file.name, public_image: false }),
    });
    // A non-2xx here would otherwise parse to a body with no `url`, and the
    // upload PUT would target `undefined` — failing downstream with nothing
    // naming this request as the cause.
    if (!assetResponse.ok) {
      throw new Error(
        `Signed-URL request for "${uploadFile.file.name}" failed with status ${assetResponse.status}`
      );
    }

    // read asset response content and upload binary to signed url
    const assetDetails = await assetResponse.json();

    // Stamp the asset classification for the manual-entry flows so it mirrors the
    // observable document's provider (backend AASM: proxy -> PROXY, self-reported
    // -> PERSON). Both flows bypass extraction, so the backend additionally allows
    // PNG/JPEG for these providers on top of PDF/plain-text -- admin/borrower can
    // attach a screenshot, not any content type. UPLOAD and other flows are left
    // for the backend to derive from the content type (so extraction routing keeps
    // working).
    if (via === 'PROXY') {
      assetDetails.classification = 'ObservableProvider.PROXY';
    } else if (via === 'SELF_REPORTED') {
      assetDetails.classification = 'ObservableProvider.PERSON';
    }
    await uploadFile.uploadBinary(assetDetails.url, { method: 'PUT' });

    const observableAsset = this.digitalAssets.createAssetFromS3Details(
      'observable-asset',
      assetDetails
    );
    // Set content type AND size from the uploaded file so the API skips its S3
    // HEAD: DigitalAsset#ensure_remote_data only skips when BOTH content_type is
    // present and size is nonzero. That HEAD is unreliable against LocalStack
    // (returns octet-stream), which then fails the ObservableAsset content-type
    // validation. Mirrors digital-assets.ts / data-transfer-agent.ts.
    // Browser MIME remains unvalidated until the API saves the asset.
    (observableAsset as DigitalAssetTrait).contentType = inferContentType(uploadFile.file);
    observableAsset.size = uploadFile.file.size;
    // WarpDrive mutation builder (Phase 2.7) — replaces observableAsset.save().
    await saveRecord(this.store, observableAsset);

    const contentType = observableAsset.contentType;
    const provider = observableAsset.classification;

    const observableDocument = this.store.createRecord('observable-document', {
      observableAsset,
      person,
      batch,
      contentType,
      provider,
      via,
    }) as ObservableDocumentModel;
    // WarpDrive mutation builder (Phase 2.7) — replaces observableDocument.save().
    await saveRecord(this.store, observableDocument);

    return observableDocument;
  };

  updateObservableDocument = (
    attributes: ObservableDocumentAttributes
  ): ObservableDocumentModel => {
    const { id, ...attributeValues } = attributes;

    const payload: ObservableDocumentPayload = {
      data: [
        {
          type: 'observable-document',
          id,
          attributes: attributeValues as SerializableRecord,
        },
      ],
    };

    const pushed = this.store.push(payload as Parameters<StoreService['push']>[0]);

    if (!pushed) {
      throw new Error('Failed to update observable document.');
    }

    const [observableDocument] = pushed as ObservableDocumentModel[];

    if (!observableDocument) {
      throw new Error('Observable document missing from store response.');
    }

    return observableDocument;
  };
}
